/**
 * AdminHealthTab — Platform Console · tenant-admin / billing-action health.
 *
 * iter87.237 (renamed iter87.240 — collided with the pre-existing
 * `AdminOpsTab`/"Comms Monitor" component in SiteAdmin.jsx, which this
 * file is NOT related to) — lightweight sibling of GameOpsTab, scoped
 * to `admin_health/*` (co-admin permission schema drift + Pro+/Paddle
 * billing integrity). Registry: memory/admin_health_registry.md.
 * No healers ship yet — detector-only, so this is Health + Issues only
 * (no Tools tab, unlike Game Ops).
 */
import { useEffect, useState } from "react";
import { ShieldAlert, RefreshCw, HeartPulse, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { SubTabs } from "@/components/site-admin/values-registry/RegistryShared";

const POSTURE_PILL = {
    blocked: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    warn:    "bg-amber-500/15 text-amber-300 border-amber-500/40",
    notice:  "bg-sky-500/15 text-sky-300 border-sky-500/40",
    ok:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

function Pill({ cls, children, testId }) {
    return (
        <span className={`pill text-[9px] font-bold uppercase border ${cls}`} data-testid={testId}>
            {children}
        </span>
    );
}

function HealthView({ summary, registry }) {
    const rows = registry?.rows || [];
    return (
        <div className="space-y-4" data-testid="admin-health-view">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-health-tiles">
                {(summary?.domains || []).map((d) => (
                    <div
                        key={d.domain}
                        className="brutal-card p-3 space-y-1"
                        data-testid={`admin-health-tile-${d.domain.split("/")[1]}`}
                    >
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-bold">{d.label}</div>
                        <Pill cls={POSTURE_PILL[d.posture] || POSTURE_PILL.warn} testId={`admin-health-posture-${d.domain.split("/")[1]}`}>
                            {d.posture}
                        </Pill>
                        <div className="text-[9px] text-zinc-600">
                            {d.findings.filter((f) => f.posture !== "ok").length || "no"} open finding(s)
                        </div>
                    </div>
                ))}
                {!summary && <div className="text-xs text-zinc-500 italic col-span-full">Running detectors…</div>}
            </div>

            <div className="brutal-card p-4">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200 mb-1">
                    Capability registry
                </h3>
                <p className="text-[10px] text-zinc-500 mb-3">
                    Source of truth: <code>admin_health_registry.md</code>. Detector-only for now —
                    no auto-healers ship in this pass.
                </p>
                <div className="space-y-1.5" data-testid="admin-health-registry-rows">
                    {rows.map((r) => (
                        <div key={r.name} className="flex items-center gap-2 flex-wrap border border-zinc-800 rounded p-2 text-[11px] bg-black/30" data-testid={`admin-health-registry-row-${r.name}`}>
                            <code className="font-bold text-white">{r.name}</code>
                            <Pill cls="bg-zinc-800 text-zinc-300 border-zinc-700">{r.kind}</Pill>
                            <Pill cls="bg-emerald-900/40 text-emerald-200 border-emerald-700">{r.status}</Pill>
                            <span className="text-[9px] text-zinc-600">{r.domain} · {r.canonical_home}</span>
                        </div>
                    ))}
                    {rows.length === 0 && (
                        <div className="text-[10px] text-zinc-500 italic px-2 py-3">Loading registry…</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ConflictingAdminFlagsDetails({ onResolved }) {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        const r = await api.adminHealthConflictingAdminFlagsDetails();
        setRows(r.rows || []);
    };
    useEffect(() => { load(); }, []);

    const resolve = async (row) => {
        setBusy(row.participant_id);
        try {
            await api.siteDemoteCoAdmin(row.league_id, row.participant_id);
            await load();
            onResolved?.();
        } finally {
            setBusy(null);
        }
    };

    if (rows === null) {
        return <div className="text-[10px] text-zinc-500 italic px-2 py-2">Loading affected records…</div>;
    }
    if (rows.length === 0) {
        return <div className="text-[10px] text-emerald-400 px-2 py-2">All clear now.</div>;
    }
    return (
        <div className="w-full mt-1.5 space-y-1.5" data-testid="admin-health-conflicting-admin-flags-details">
            {rows.map((row) => (
                <div
                    key={row.participant_id}
                    className="flex items-center justify-between gap-2 flex-wrap border border-zinc-800 rounded p-2 bg-zinc-950/60"
                    data-testid={`conflicting-admin-flags-row-${row.participant_id}`}
                >
                    <div className="text-[10px] text-zinc-300">
                        <span className="font-bold text-white">{row.name}</span>
                        {" "}— {row.kind === "dual_role"
                            ? <>is both primary admin and co-admin in <span className="text-zinc-400">{row.league_name}</span></>
                            : <>co-admin flag points at a deleted league ({row.league_id})</>}
                    </div>
                    {row.kind === "dual_role" && (
                        <button
                            type="button"
                            onClick={() => resolve(row)}
                            disabled={busy === row.participant_id}
                            data-testid={`conflicting-admin-flags-fix-btn-${row.participant_id}`}
                            className="brutal-btn text-[9px] py-1 px-2"
                        >
                            {busy === row.participant_id ? "Clearing…" : "Clear co-admin flag"}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

function IssuesView({ summary, onRefresh }) {
    const issues = summary?.issues || [];
    return (
        <div className="space-y-3" data-testid="admin-health-issues-view">
            {!summary && <div className="text-xs text-zinc-500 italic">Running detectors…</div>}
            {summary && issues.length === 0 && (
                <div className="border border-emerald-900/50 bg-emerald-950/10 rounded p-4 text-xs text-emerald-300" data-testid="admin-health-issues-empty">
                    All clear — every detector reports clean.
                </div>
            )}
            {issues.map((i) => (
                <div key={i.id} className="flex items-start gap-2 flex-wrap border border-zinc-800 rounded p-2.5 text-[11px] bg-black/30" data-testid={`admin-health-issue-${i.id}`}>
                    <Pill cls={POSTURE_PILL[i.posture] || POSTURE_PILL.warn}>{i.posture}</Pill>
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-white">{i.label}
                            <span className="ml-2 text-[9px] font-normal text-zinc-600">{i.domain_label}</span>
                        </div>
                        <p className="text-[10px] text-zinc-400 mt-0.5">{i.detail}</p>
                        {i.id === "conflicting_admin_flags" && (
                            <ConflictingAdminFlagsDetails onResolved={onRefresh} />
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function AdminHealthTab() {
    const [subTab, setSubTab] = useState("health");
    const [summary, setSummary] = useState(null);
    const [registry, setRegistry] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [s, r] = await Promise.all([api.adminHealthSummary(), api.adminHealthRegistry()]);
            setSummary(s);
            setRegistry(r);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-4" data-testid="admin-health-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-zinc-400" />
                    <h2 className="font-display font-extrabold uppercase tracking-wide text-zinc-200">Admin Health</h2>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    data-testid="admin-health-refresh-btn"
                    className="brutal-btn text-[10px] py-1.5 px-3 flex items-center gap-1.5"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <SubTabs
                testPrefix="admin-health-subtab"
                active={subTab}
                onChange={setSubTab}
                items={[
                    { k: "health", label: "Health", icon: HeartPulse },
                    { k: "issues", label: "Issues", icon: ListChecks },
                ]}
            />

            {subTab === "health" && <HealthView summary={summary} registry={registry} />}
            {subTab === "issues" && <IssuesView summary={summary} onRefresh={load} />}
        </div>
    );
}
